'use client';

import { useTranslations } from 'next-intl';
import { useState } from 'react';
import type { InboxNotification } from '@/lib/api/notifications/inbox-client';
import { useInboxUnreadCount } from '@/lib/api/notifications/inbox-hooks';
import { NotificationsDrawer } from '@/lib/components/NotificationsDrawer';
import { Icon } from '@/lib/ui-kit/icons';
import { cn } from '@/lib/ui-kit/utils';
import { Button } from '@/lib/ui-kit-v2/button';
import { formatBadgeCount } from '@/lib/utils/formatBadgeCount';

interface NotificationsBellButtonProps {
  /** Manual count override — for visual previews/tests. Production reads from the hook. */
  countOverride?: number;
  /** When true (default), opens a Drawer on click. Set false to disable in previews. */
  withDrawer?: boolean;
  /** Dev-only: forwarded to NotificationsDrawer for static preview content. */
  drawerMockOverride?: { items: InboxNotification[]; unreadCount: number };
}

export function NotificationsBellButton({
  countOverride,
  withDrawer = true,
  drawerMockOverride,
}: NotificationsBellButtonProps) {
  const t = useTranslations('nav');
  const [open, setOpen] = useState(false);
  const isMocked = countOverride !== undefined || drawerMockOverride !== undefined;
  const { data } = useInboxUnreadCount({ enabled: !isMocked });
  const count = countOverride ?? drawerMockOverride?.unreadCount ?? data?.count ?? 0;
  const hasUnread = count > 0;
  const badgeText = hasUnread ? formatBadgeCount(count) : '';
  // "99+" needs more horizontal room — render as a pill. Single/double digits
  // get a perfectly round circle.
  const isWideBadge = badgeText.length > 2;

  return (
    <>
      <Button
        appearance="icon"
        size="M"
        onClick={withDrawer ? () => setOpen(true) : undefined}
        aria-label={t('notifications')}
        className="relative"
      >
        <Icon name="Notification" size={24} />
        {hasUnread ? (
          <span
            aria-hidden
            className={cn(
              'pointer-events-none absolute top-[4px] right-[4px] flex h-[16px] items-center justify-center rounded-full bg-v2-btn-premium-red-light font-medium font-sans text-[10px] leading-none text-white',
              isWideBadge ? 'min-w-[22px] px-[5px]' : 'w-[16px]',
            )}
          >
            {badgeText}
          </span>
        ) : null}
      </Button>
      {withDrawer ? (
        <NotificationsDrawer open={open} onOpenChange={setOpen} mockOverride={drawerMockOverride} />
      ) : null}
    </>
  );
}
